import { HttpClient, HttpErrorResponse } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Observable, throwError } from 'rxjs';
import { catchError, map, tap } from 'rxjs/operators';

import { AlcmeonContextService } from 'src/app/core/alcmeon-context/alcmeon-context.service';
import { SelfV2 } from 'src/app/core/alcmeon-context/models/self-v2.model';
import {
  API_URL_PREFIX,
  BasicService,
  JSON_CONTENT_TYPE_HEADER,
  RoutePaths,
} from 'src/app/shared/services/basic.service';
import { ErrorHandlingService } from 'src/app/shared/services/error-handling.service';
import { alcmeonSupportedLanguages } from './supported-languages.const';

const COMPANIES_WITH_LEGACY_SAML_ENDPOINTS = [88]; // 88 is Carrefour company id. The behaviour is very specific and will not change.
/**
 * Login service used only in mobile (application and website)
 */
@Injectable({
  providedIn: 'root',
})
export class LoginService {
  private GET_SELF_URL_RE = new RegExp('/api/1.0/companies/[0-9]/users/self');

  constructor(
    private http: HttpClient,
    private alcmeonContext: AlcmeonContextService,
    private basicService: BasicService,
    private ehs: ErrorHandlingService
  ) {}

  initiateSSO(afterLoginURL: string) {
    const { companyId, samlConfigId } = this.alcmeonContext;
    const suffix = COMPANIES_WITH_LEGACY_SAML_ENDPOINTS.includes(companyId)
      ? `/saml/initiate-sso`
      : `/saml/${samlConfigId}/initiate-sso`;
    const initiateSSOURL = `${API_URL_PREFIX}/${companyId}${suffix}`;
    const data = { redirect_to: afterLoginURL };
    return this.http.post<InitiateSSOResponse>(initiateSSOURL, data).pipe(
      map(response => response.redirect_to),
      this.ehs.catchAlertAndThrowHttpError()
    );
  }

  initiateSLO(afterLogoutURL: string) {
    const { companyId, samlConfigId } = this.alcmeonContext;
    const suffix = COMPANIES_WITH_LEGACY_SAML_ENDPOINTS.includes(companyId)
      ? `/saml/initiate-slo`
      : `/saml/${samlConfigId}/initiate-slo`;
    const initiateSLOURL = `${API_URL_PREFIX}/${companyId}${suffix}`;
    const data = { redirect_to: afterLogoutURL };
    return this.http.post<InitiateSSOResponse>(initiateSLOURL, data).pipe(
      map(response => response.redirect_to),
      this.ehs.catchAlertAndThrowHttpError()
    );
  }

  convertCompanyCode(companyCode): Observable<CompanyResponse> {
    return this.http.get<CompanyResponse>(`${API_URL_PREFIX}/api/2.0/companies/${companyCode}`).pipe(
      tap(company => this.alcmeonContext.setCompanyInfo(company)),
      catchError(error => this.handleCompanyError(error))
    );
  }

  logoutProcess() {
    if (this.alcmeonContext.isSaml) {
      const completeLogoutSearchParams = new URLSearchParams();
      completeLogoutSearchParams.append('after-logout-route', RoutePaths.CompanySelection);
      const samlResultURL = `${this.basicService.fullURLForRoutePath(
        RoutePaths.SamlResult
      )}?${completeLogoutSearchParams.toString()}`;
      this.initiateSLO(samlResultURL).subscribe(
        samlLogoutURL => (this.basicService.window.location.href = samlLogoutURL),
        () => {
          this.basicService.navigateRoot(RoutePaths.CompanySelection);
        }
      ); // never mind, the use will login again anyway
    } else {
      this.logout().subscribe(() => {
        this.alcmeonContext.clearJwt();
        this.basicService.navigateRoot(RoutePaths.Login);
      });
    }
  }

  logout() {
    const logoutURL = `${API_URL_PREFIX}/${this.alcmeonContext.companyId}/logout`;
    return this.http.post<void>(logoutURL, null).pipe(this.ehs.catchAlertAndThrowHttpError());
  }

  login(email, password): Observable<LoginResponse> {
    const companyId = this.alcmeonContext.companyId;
    const loginURL = `${API_URL_PREFIX}/${companyId}/login`;
    const data = { email, password };
    return this.http.post<LoginResponse>(loginURL, data, JSON_CONTENT_TYPE_HEADER).pipe(
      tap((response: LoginResponse) => this.alcmeonContext.setJwt(response.jwt)),
      catchError(error => this.handleLoginError(error))
    );
  }

  isSelfURL(url) {
    return this.GET_SELF_URL_RE.test(url);
  }

  getSelf(): Observable<SelfV2[]> {
    const companyId = this.alcmeonContext.companyId;
    const selfURL = `${API_URL_PREFIX}/api/2.0/companies/${companyId}/users/self`;
    return this.http.get<SelfV2[]>(selfURL).pipe(
      tap(async response => {
        const loggedUser = ((Array.isArray(response) && response[0]) || response) as SelfV2;
        try {
          const { lang } = loggedUser;
          const hasSupportedLanguage = alcmeonSupportedLanguages.includes(lang);
          if (!hasSupportedLanguage) {
            console.info(`Language "${lang}" not supported, fallback to english.`);
          }
          const languageCode = hasSupportedLanguage ? lang : 'en';
          loggedUser.lang = languageCode;
        } catch {
          console.error('Unable to interpret the user language:', loggedUser);
        }
        if (!(await this.alcmeonContext.getCompanyUrlNameFromStorage(companyId))) {
          this.alcmeonContext.storeCompanyUrlName(companyId, loggedUser);
        }
      })
    ); // errors are handled by AuthGuard
  }

  private handleCompanyError(error: HttpErrorResponse) {
    if (error.status === 400) {
      this.ehs.modalAlertError(error, 'm/login/error/company-code-not-found');
    } else {
      this.ehs.toastAlertUnknownError(error);
    }
    return throwError(error);
  }

  private handleLoginError(error: HttpErrorResponse) {
    const knownErrorReasons = {
      'invalid-user-or-password': 'm/login/error/invalid-user-or-password',
    };
    this.ehs.alertHttpError(error, knownErrorReasons);
    return throwError(error);
  }
}

interface LoginResponse {
  jwt: string;
  // latest_hash is not used by the native app
}

export interface CompanyResponse {
  password_max_failed_attempts: number;
  url_name: string;
  id: number;
  uses_saml: boolean;
  login_options?: [
    {
      id: number;
      type: number;
      label: string;
      saml_configuration_id: number;
      is_active: boolean;
    }
  ];
}

interface InitiateSSOResponse {
  redirect_to: string;
}
